import { ethers, namehash } from 'ethers';
import { queryLogs } from './common';
import { ACTIVE_DEPLOYMENT } from '../contracts/common';

// Contract Deplyoments
import RegistryDeployment from '../../contracts/deployments/Registry.json';

// Contract ABIs
import RegistryABI from '../../contracts/abis/Registry.json';
import ResolverABI from '../../contracts/abis/Resolver.json';

export const getFirstLevelDomains = async (): Promise<string[]> => {
  const domains = await querySubdomains('cam');
  return domains.map(domain => domain.name.split('.')[0]);
};

export const querySubdomains = async (parent: string): Promise<Domain[]> => {
  const parentHash = namehash(parent);
  const iface = new ethers.Interface(RegistryABI);

  const newOwnerLogs = await queryLogs(
    RegistryDeployment[ACTIVE_DEPLOYMENT].address,
    'NewOwner(bytes32,string,address)',
    [parentHash],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const latestOwners = new Map<
    string,
    { label: string; parent: string; owner: string; blockNumber: number }
  >();

  newOwnerLogs.forEach(log => {
    const parsedLog = iface.parseLog(log);
    if (parsedLog?.args) {
      const [parent, label, owner] = parsedLog.args.map((arg: any) =>
        arg.toString(),
      );
      const key = namehash(label + '.' + parent);
      const blockNumber = log.blockNumber;
      latestOwners.set(key, { label, parent, owner, blockNumber });
    }
  });

  latestOwners.forEach(async (log, key) => {
    const transferLogs = await queryLogs(
      RegistryDeployment[ACTIVE_DEPLOYMENT].address,
      'Transfer(bytes32,address)',
      [key],
      log.blockNumber,
    );

    transferLogs.forEach(transferLog => {
      const parsedLog = iface.parseLog(transferLog);
      if (parsedLog?.args) {
        const [node, owner] = parsedLog.args.map((arg: any) => arg.toString());
        if (transferLog.blockNumber > log.blockNumber) {
          latestOwners.set(node, {
            label: log.label,
            parent: log.parent,
            owner,
            blockNumber: transferLog.blockNumber,
          });
        }
      }
    });
  });

  const domains: Domain[] = [];

  latestOwners.forEach(value => {
    domains.push({
      name: value.label + '.' + parent,
      accessRights: 'owner',
      managers: [],
      subdomains: [],
    });
  });

  return domains;
};

export const getDomainTree = async (): Promise<DomainTree | undefined> => {
  const CAM_NODE =
    '0xae699f70ed198095f47e32ff39f1f2a31565a531bc8a8f2dd472afdffe7a1a9d';

  const logs = await queryLogs(
    RegistryDeployment[ACTIVE_DEPLOYMENT].address,
    'NewOwner(bytes32,string,address)',
    [],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const transferLogs = await queryLogs(
    RegistryDeployment[ACTIVE_DEPLOYMENT].address,
    'Transfer(bytes32,address)',
    [],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const iface = new ethers.Interface(RegistryABI);

  const latestOwners = new Map<string, string[]>();

  logs.forEach(log => {
    const parsedLog = iface.parseLog(log);
    if (parsedLog?.args) {
      const [label, parent, owner] = parsedLog.args.map((arg: any) =>
        arg.toString(),
      );
      const key = `${label}-${parent}`;
      latestOwners.set(key, [label, parent, owner]);
    }
  });

  const argsArray: string[][] = Array.from(latestOwners.values());

  const transferArray: string[][] = transferLogs
    .map(log => {
      const parsedLog = iface.parseLog(log);
      return parsedLog?.args?.map((arg: any) => arg.toString());
    })
    .filter((args): args is string[] => args !== undefined);

  const buildDomainTree = (logs: string[][]): DomainTree | undefined => {
    let domainTree: DomainTree | undefined = undefined;

    const findAndAddSubdomain = (
      node: DomainTree,
      parentHash: string,
      label: string,
      owner: string,
    ): boolean => {
      if (node.hash === parentHash) {
        const name = label + '.' + node.name;
        const hash = ethers.namehash(name);
        const subdomain = {
          name: name,
          owner: owner,
          parentHash,
          hash: hash,
          subdomains: [],
        };
        node.subdomains.push(subdomain);
        return true;
      }
      for (const sub of node.subdomains) {
        if (findAndAddSubdomain(sub, parentHash, label, owner)) {
          return true;
        }
      }
      return false;
    };

    for (const log of logs) {
      const [parent, label, owner] = log;

      if (
        parent ===
        '0x0000000000000000000000000000000000000000000000000000000000000000'
      ) {
        domainTree = {
          name: label,
          owner,
          parentHash: parent,
          hash: CAM_NODE,
          subdomains: [],
        };
      } else {
        if (domainTree) {
          if (!findAndAddSubdomain(domainTree, parent, label, owner)) {
            console.warn(`Parent hash ${parent} not found for label ${label}`);
          }
        } else {
          throw new Error('Domain tree is not initialized.');
        }
      }
    }

    return domainTree;
  };

  const updateDomainOwnerByHashInTree = (
    domainTree: DomainTree,
    targetHash: string,
    newOwner: string,
  ): boolean => {
    if (domainTree.hash === targetHash) {
      domainTree.owner = newOwner;
      return true;
    }

    for (const subdomain of domainTree.subdomains) {
      if (updateDomainOwnerByHashInTree(subdomain, targetHash, newOwner)) {
        return true;
      }
    }

    return false;
  };

  if (argsArray) {
    const domainTree = buildDomainTree(argsArray);
    if (domainTree) {
      for (const index in transferArray) {
        const [targetHash, newOwner] = transferArray[index];
        updateDomainOwnerByHashInTree(domainTree, targetHash, newOwner);
      }
      return domainTree;
    }
  }
  return undefined;
};

export const getManagers = async (
  domain: string,
  resolverAddress: string,
): Promise<string[]> => {
  const logs = await queryLogs(
    resolverAddress,
    'OperatorApproved(bytes32,address,bool)',
    [],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const iface = new ethers.Interface(ResolverABI);

  const node = namehash(domain);

  const managers: string[] = [];

  logs.map(log => {
    const decodedLog = iface.parseLog(log);
    const args = decodedLog?.args;
    if (args && args[0].includes(node)) {
      if (args[2]) {
        if (!managers.includes(args[1])) {
          managers.push(args[1]);
        }
      } else {
        const index = managers.indexOf(args[1]);
        if (index > -1) {
          managers.splice(index, 1);
        }
      }
    }
  });

  return managers;
};

export const getDomainsByManager = async (
  managerAddress: string,
  resolverAddress: string,
): Promise<string[]> => {
  const logs = await queryLogs(
    resolverAddress,
    'OperatorApproved(bytes32,address,bool)',
    [null, ethers.zeroPadValue(managerAddress, 32)],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const iface = new ethers.Interface(ResolverABI);

  const domains: string[] = [];

  logs.map(log => {
    const decodedLog = iface.parseLog(log);
    const args = decodedLog?.args;
    if (args && args[1].toLowerCase() === managerAddress.toLowerCase()) {
      if (args[2]) {
        if (!domains.includes(args[0])) {
          domains.push(args[0]);
        }
      } else {
        const index = domains.indexOf(args[0]);
        if (index > -1) {
          domains.splice(index, 1);
        }
      }
    }
  });

  return domains;
};

export const getOwnersByOperator = async (
  operator: string,
): Promise<string[]> => {
  const logs = await queryLogs(
    RegistryDeployment[ACTIVE_DEPLOYMENT].address,
    'ApprovalForAll(address,address,bool)',
    [null, ethers.zeroPadValue(operator, 32)],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const iface = new ethers.Interface(RegistryABI);

  const owners: string[] = [];

  logs.map(log => {
    const decodedLog = iface.parseLog(log);
    const args = decodedLog?.args;
    if (args && args[2]) {
      // approved
      if (!owners.includes(args[0])) {
        owners.push(args[0]);
      }
    } else {
      if (args) {
        const index = owners.indexOf(args[0]);
        if (index > -1) {
          owners.splice(index, 1);
        }
      }
    }
  });

  return owners;
};

export const getOperatorsByOwner = async (owner: string): Promise<string[]> => {
  const logs = await queryLogs(
    RegistryDeployment[ACTIVE_DEPLOYMENT].address,
    'ApprovalForAll(address,address,bool)',
    [ethers.zeroPadValue(owner, 32), null],
    RegistryDeployment[ACTIVE_DEPLOYMENT].block,
  );

  const iface = new ethers.Interface(RegistryABI);

  const operators: string[] = [];

  logs.forEach(log => {
    const decodedLog = iface.decodeEventLog(
      'ApprovalForAll',
      log.data,
      log.topics,
    );
    const { operator, approved } = decodedLog;

    if (approved) {
      if (!operators.includes(operator)) {
        operators.push(operator);
      }
    } else {
      const index = operators.indexOf(operator);
      if (index > -1) {
        operators.splice(index, 1);
      }
    }
  });

  return operators;
};

// Helper functions
// const _paseLabelAndParentHash = (label: string, parentHash: string) => {
//   const labelhash = ethers.keccak256(ethers.toUtf8Bytes(label));
//   const encoder = AbiCoder.defaultAbiCoder();
//   return encoder.encode(['bytes32', 'bytes32'], [parentHash, labelhash]);
// };
